import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Navigation,
  MapPin,
  ArrowUp,
  Clock,
  Footprints,
  ChevronRight,
  Compass,
  AlertCircle,
  Utensils,
  Droplet,
  Car,
  ShieldAlert,
  Package,
  Info,
  Users,
  Flag,
  Ticket,
} from 'lucide-react';

// POI data (same as Map page - would be shared in real app)
const POI_CATEGORIES = {
  food: { icon: Utensils, label: 'Food & Drinks', color: 'bg-orange-500' },
  restrooms: { icon: Droplet, label: 'Restrooms', color: 'bg-blue-500' },
  parking: { icon: Car, label: 'Parking', color: 'bg-gray-500' },
  tickets: { icon: Ticket, label: 'Tickets', color: 'bg-purple-500' },
  medical: { icon: ShieldAlert, label: 'First Aid', color: 'bg-red-500' },
  merchandise: { icon: Package, label: 'Merchandise', color: 'bg-pink-500' },
  info: { icon: Info, label: 'Information', color: 'bg-green-500' },
  vip: { icon: Users, label: 'VIP Areas', color: 'bg-yellow-500' },
  grandstand: { icon: Flag, label: 'Grandstands', color: 'bg-indigo-500' },
};

type POICategory = keyof typeof POI_CATEGORIES;

interface POI {
  id: string;
  name: string;
  category: POICategory;
  description: string;
  x: number;
  y: number;
}

const trackPOIs: POI[] = [
  { id: 'food-1', name: 'Main Concession Stand', category: 'food', description: 'Full menu, hot food, drinks', x: 25, y: 35 },
  { id: 'food-2', name: 'Pit Lane Cafe', category: 'food', description: 'Coffee, snacks, sandwiches', x: 50, y: 60 },
  { id: 'restroom-1', name: 'Main Entrance Restrooms', category: 'restrooms', description: 'Accessible facilities', x: 15, y: 40 },
  { id: 'restroom-2', name: 'Pit Building Restrooms', category: 'restrooms', description: 'Ground floor', x: 52, y: 55 },
  { id: 'medical-1', name: 'First Aid Station', category: 'medical', description: '24/7 medical support', x: 55, y: 50 },
  { id: 'merch-1', name: 'Official Merchandise Store', category: 'merchandise', description: 'Apparel, accessories', x: 22, y: 45 },
  { id: 'info-1', name: 'Information Center', category: 'info', description: 'Maps, lost & found', x: 20, y: 42 },
  { id: 'grandstand-1', name: 'Grandstand A - Start/Finish', category: 'grandstand', description: 'Best view of start/finish', x: 35, y: 30 },
  { id: 'grandstand-2', name: 'Grandstand B - Hairpin', category: 'grandstand', description: 'Great overtaking spot', x: 75, y: 45 },
];

// Quick destinations
const quickDestinations = [
  { id: 'restroom-1', label: 'Nearest Restroom', icon: Droplet, color: 'bg-blue-500' },
  { id: 'food-1', label: 'Nearest Food', icon: Utensils, color: 'bg-orange-500' },
  { id: 'medical-1', label: 'First Aid', icon: ShieldAlert, color: 'bg-red-500' },
  { id: 'info-1', label: 'Information', icon: Info, color: 'bg-green-500' },
];

export function NavigatePage() {
  const [searchParams] = useSearchParams();
  const destinationId = searchParams.get('to');

  const [destination, setDestination] = useState<POI | null>(null);
  const [heading, setHeading] = useState<number>(0);
  const [hasCompass, setHasCompass] = useState(true);

  useEffect(() => {
    if (destinationId) {
      const poi = trackPOIs.find((p) => p.id === destinationId);
      if (poi) setDestination(poi);
    }
  }, [destinationId]);

  // Compass simulation (in real app would use DeviceOrientationEvent)
  useEffect(() => {
    // Check if device orientation is available
    if (typeof DeviceOrientationEvent !== 'undefined') {
      const handleOrientation = (e: DeviceOrientationEvent) => {
        if (e.alpha !== null) {
          setHeading(e.alpha);
        }
      };

      // Request permission on iOS
      if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
        (DeviceOrientationEvent as any).requestPermission()
          .then((response: string) => {
            if (response === 'granted') {
              window.addEventListener('deviceorientation', handleOrientation);
            } else {
              setHasCompass(false);
            }
          })
          .catch(() => setHasCompass(false));
      } else {
        window.addEventListener('deviceorientation', handleOrientation);
      }

      return () => {
        window.removeEventListener('deviceorientation', handleOrientation);
      };
    } else {
      // Simulate compass movement for demo
      const interval = setInterval(() => {
        setHeading((prev) => (prev + 1) % 360);
      }, 100);
      return () => clearInterval(interval);
    }
  }, []);

  // Calculate mock distance and time
  const getDistanceAndTime = (poi: POI) => {
    // Mock calculation based on position
    const distance = Math.round(Math.sqrt(poi.x ** 2 + poi.y ** 2) * 5);
    const minutes = Math.ceil(distance / 80); // 80m per minute walking
    return { distance, minutes };
  };

  return (
    <div className="py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Navigate</h1>
          <p className="text-gray-600">Find your way around the track</p>
        </div>

        {destination ? (
          // Navigation Mode
          <div className="space-y-6">
            {/* Destination Card */}
            <div className="card p-6">
              <div className="flex items-center gap-4">
                <div
                  className={`w-14 h-14 ${
                    POI_CATEGORIES[destination.category].color
                  } rounded-xl flex items-center justify-center text-white`}
                >
                  {(() => {
                    const Icon = POI_CATEGORIES[destination.category].icon;
                    return <Icon size={28} />;
                  })()}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500">Navigating to</p>
                  <h2 className="text-xl font-bold text-gray-900">{destination.name}</h2>
                  <p className="text-gray-600">{destination.description}</p>
                </div>
              </div>

              <div className="flex gap-4 mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 text-gray-600">
                  <Footprints size={18} />
                  <span>{getDistanceAndTime(destination).distance}m</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock size={18} />
                  <span>{getDistanceAndTime(destination).minutes} min walk</span>
                </div>
              </div>
            </div>

            {/* Compass */}
            <div className="card p-8">
              <div className="relative w-64 h-64 mx-auto">
                {/* Compass Ring */}
                <div
                  className="absolute inset-0 rounded-full border-4 border-gray-200"
                  style={{ transform: `rotate(${-heading}deg)` }}
                >
                  {/* Cardinal directions */}
                  {['N', 'E', 'S', 'W'].map((dir, i) => (
                    <div
                      key={dir}
                      className="absolute w-8 h-8 flex items-center justify-center font-bold text-gray-400"
                      style={{
                        top: i === 0 ? '0' : i === 2 ? 'auto' : '50%',
                        bottom: i === 2 ? '0' : 'auto',
                        left: i === 3 ? '0' : i === 1 ? 'auto' : '50%',
                        right: i === 1 ? '0' : 'auto',
                        transform:
                          i === 0 || i === 2
                            ? 'translateX(-50%)'
                            : 'translateY(-50%)',
                      }}
                    >
                      <span style={{ transform: `rotate(${heading}deg)` }}>{dir}</span>
                    </div>
                  ))}
                </div>

                {/* Direction Arrow */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="w-32 h-32 rounded-full bg-red-600 flex items-center justify-center"
                    style={{ transform: `rotate(45deg)` }} // Would calculate actual direction
                  >
                    <ArrowUp size={48} className="text-white" style={{ transform: 'rotate(-45deg)' }} />
                  </div>
                </div>

                {/* Center dot */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-4 h-4 bg-red-600 rounded-full border-2 border-white shadow-lg" />
                </div>
              </div>

              {!hasCompass && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2 text-yellow-800 text-sm">
                  <AlertCircle size={18} />
                  <span>Compass not available. Enable device orientation for accurate directions.</span>
                </div>
              )}

              <div className="mt-6 text-center">
                <p className="text-2xl font-bold text-gray-900">Head this way</p>
                <p className="text-gray-600">Follow the arrow to reach your destination</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <Link
                to="/map"
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
              >
                <MapPin size={20} />
                View on Map
              </Link>
              <button
                onClick={() => setDestination(null)}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors"
              >
                <Navigation size={20} />
                New Destination
              </button>
            </div>
          </div>
        ) : (
          // Destination Selection Mode
          <div className="space-y-8">
            {/* Quick Destinations */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Access</h2>
              <div className="grid grid-cols-2 gap-4">
                {quickDestinations.map(({ id, label, icon: Icon, color }) => (
                  <button
                    key={id}
                    onClick={() => {
                      const poi = trackPOIs.find((p) => p.id === id);
                      if (poi) setDestination(poi);
                    }}
                    className="card p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center text-white`}>
                      <Icon size={24} />
                    </div>
                    <span className="font-medium text-gray-900">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* All Destinations */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">All Locations</h2>
              <div className="card divide-y divide-gray-100">
                {trackPOIs.map((poi) => {
                  const { icon: Icon, color } = POI_CATEGORIES[poi.category];
                  const { distance, minutes } = getDistanceAndTime(poi);
                  return (
                    <button
                      key={poi.id}
                      onClick={() => setDestination(poi)}
                      className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center text-white`}>
                        <Icon size={18} />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-gray-900">{poi.name}</p>
                        <p className="text-sm text-gray-500">{poi.description}</p>
                      </div>
                      <div className="text-right text-sm text-gray-500">
                        <p>{distance}m</p>
                        <p>{minutes} min</p>
                      </div>
                      <ChevronRight size={18} className="text-gray-400" />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Map Link */}
            <Link
              to="/map"
              className="block card p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center text-white">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">View Full Map</p>
                    <p className="text-sm text-gray-500">See all locations on the map</p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </div>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
